import React from 'react';
import {
  FaRocket, FaPuzzlePiece, FaUsers, FaMobileAlt, FaEnvelope,
  FaCode, FaPaintBrush, FaGamepad,
  FaBriefcase, FaMapMarkerAlt, FaClock,
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';

const cardIcons = [<FaRocket />, <FaPuzzlePiece />, <FaMobileAlt />, <FaUsers />];

const AboutContainer = () => {
  const { lang } = useLang();
  const t = translations[lang].about;

  const aboutCards = t.cards.map((c, i) => ({ ...c, icon: cardIcons[i] }));

  return (
    <div className="w-full min-h-screen bg-gray-950">

      {/* ── Banner ── */}
      <div className="relative w-full bg-gray-950 pt-10 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute right-0 top-0 w-[500px] h-[400px] bg-headingColor/8 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto text-center">
          <p className="text-headingColor text-xs font-bold tracking-widest uppercase mb-3">{t.eyebrow}</p>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight mb-4">{t.title}</h1>
          <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">{t.subtitle}</p>
        </div>
      </div>

      {/* ── Mission/Vision cards ── */}
      <section className="w-full bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {aboutCards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -4 }}
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-headingColor/20 hover:shadow-xl hover:shadow-headingColor/5 transition-all duration-300"
              >
                <div className="w-11 h-11 bg-headingColor/8 group-hover:bg-headingColor rounded-xl flex items-center justify-center text-headingColor group-hover:text-white text-lg transition-all duration-300 mb-5">
                  {card.icon}
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-3 tracking-tight">{card.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Timeline ── */}
      <section className="w-full bg-gray-950 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-headingColor text-xs font-bold tracking-widest uppercase mb-3">{t.timelineEyebrow}</p>
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">{t.timelineTitle}</h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/5 -translate-x-1/2 hidden md:block" />
            <div className="absolute left-4 top-0 bottom-0 w-px bg-white/5 md:hidden" />

            <div className="flex flex-col gap-0">
              {t.milestones.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -24 : 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative flex gap-8 pb-10 ${i % 2 === 0 ? 'md:flex-row-reverse md:text-right' : ''}`}
                >
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 bg-headingColor rounded-full ring-4 ring-gray-950 z-10 top-1" />
                  <div className={`pl-12 md:pl-0 ${i % 2 === 0 ? 'md:pr-10 md:w-1/2' : 'md:pl-10 md:w-1/2 md:ml-auto'}`}>
                    <span className="text-headingColor text-xs font-bold tracking-widest uppercase">{m.period}</span>
                    <h3 className="text-white font-black text-lg mt-1 mb-2 tracking-tight">{m.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Team ── */}
      <section className="w-full bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-headingColor text-xs font-bold tracking-widest uppercase mb-3">{t.teamEyebrow}</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">{t.teamTitle}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.team.map((member, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-headingColor/20 hover:shadow-xl hover:shadow-headingColor/5 transition-all duration-300 text-center"
              >
                <div className="w-20 h-20 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-center text-4xl mx-auto mb-5">
                  {member.initials}
                </div>
                <h3 className="text-gray-900 font-black text-base mb-1 tracking-tight">{member.role}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mt-2">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Careers ── */}
      <section className="w-full bg-gray-950 py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-headingColor text-xs font-bold tracking-widest uppercase mb-3">{t.careersEyebrow}</p>
              <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">{t.careersTitle}</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-md">{t.careersSubtitle}</p>
            </motion.div>
          </div>

          <div className="flex flex-col gap-3">
            {t.openings.map((job, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="group bg-gray-900 border border-white/5 hover:border-headingColor/20 rounded-xl px-6 py-5 flex items-center justify-between gap-4 cursor-pointer transition-all duration-200 hover:bg-gray-900/80"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-headingColor/10 group-hover:bg-headingColor rounded-lg flex items-center justify-center transition-colors duration-300">
                    <FaBriefcase className="text-headingColor group-hover:text-white transition-colors duration-300 text-sm" />
                  </div>
                  <div>
                    <h4 className="text-white font-black text-base tracking-tight">{job.title}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1 text-gray-600 text-xs"><FaClock size={9} />{job.type}</span>
                      <span className="flex items-center gap-1 text-gray-600 text-xs"><FaMapMarkerAlt size={9} />{job.location}</span>
                    </div>
                  </div>
                </div>
                <a
                  href="mailto:info@harmanplay.com"
                  onClick={e => e.stopPropagation()}
                  className="flex-shrink-0 bg-headingColor hover:bg-red-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all duration-200"
                >
                  {t.applyBtn}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact CTA ── */}
      <section className="w-full bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="bg-gray-950 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '50px 50px' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[200px] bg-headingColor/12 rounded-full blur-[80px] pointer-events-none" />

            <div className="relative z-10">
              <div className="w-14 h-14 bg-headingColor/15 border border-headingColor/25 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <FaEnvelope className="text-headingColor text-xl" />
              </div>
              <h3 className="text-3xl md:text-4xl font-black text-white mb-3 tracking-tight">{t.contactTitle}</h3>
              <p className="text-gray-500 mb-8 max-w-sm mx-auto text-sm leading-relaxed">{t.contactSubtitle}</p>
              <a
                href="mailto:info@harmanplay.com"
                className="inline-flex items-center gap-2.5 bg-headingColor hover:bg-red-700 text-white px-8 py-3.5 rounded-xl font-bold text-sm shadow-2xl shadow-red-950/50 transition-all duration-200 hover:-translate-y-0.5"
              >
                <FaEnvelope /> {t.contactBtn}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutContainer;
